import React, { useState } from 'react';
import UICollapsibleSection from '@/components/shared/UICollapsibleSection';
import UIButton from '@/components/shared/UIButton';
import UISelect from '@/components/shared/UISelect';
import {
  type TicketDetails,
  type UpdateTicketDto,
  type TicketStatus,
  type TicketPriority,
  type TicketCategory,
  type TicketDictionariesResponse
} from '@/types/tickets';
import './TicketEditSection.scss';

export interface TicketEditSectionProps {
  /**
   * Об'єкт тікета з деталями
   */
  ticket?: TicketDetails | null;

  /**
   * Словники для селектів
   */
  dictionaries?: TicketDictionariesResponse | null;

  /**
   * Стан завантаження
   */
  loading?: boolean;

  /**
   * Контрольований стан згорнутості
   */
  collapsed?: boolean;

  /**
   * Додатковий CSS клас
   */
  className?: string;

  /**
   * Обробник зміни стану згорнутості
   */
  onToggleCollapse?: (collapsed: boolean) => void;

  /**
   * Обробник збереження змін
   */
  onSave?: (data: UpdateTicketDto) => void;

  /**
   * Обробник очищення форми
   */
  onClear?: () => void;

  /**
   * Обробник закриття тікета
   */
  onCloseTicket?: () => void;

  /**
   * Обробник видалення тікета
   */
  onDeleteTicket?: () => void;

  /**
   * Обробник повторного відкриття тікета
   */
  onReopenTicket?: () => void;

  /**
   * Стани завантаження для кнопок
   */
  loadingStates?: {
    saving?: boolean;
    closing?: boolean;
    deleting?: boolean;
    reopening?: boolean;
  };
}

const baseClass = 'TicketEditSection';

/**
 * Компонент для редагування тікета з формою та діями
 */
const TicketEditSection: React.FC<TicketEditSectionProps> = ({
                                                               ticket,
                                                               dictionaries,
                                                               loading = false,
                                                               collapsed,
                                                               className = '',
                                                               onToggleCollapse,
                                                               onSave,
                                                               onClear,
                                                               onCloseTicket,
                                                               onDeleteTicket,
                                                               onReopenTicket,
                                                               loadingStates = {},
                                                             }) => {
  // Локальний стан форми
  const [formData, setFormData] = useState<UpdateTicketDto>({
    category: ticket?.category,
    status: ticket?.status,
    priority: ticket?.priority,
  });

  const [dueDate, setDueDate] = useState<string>(
    ticket?.updatedAt ? ticket.updatedAt.split('T')[0] : ''
  );

  const [note, setNote] = useState<string>('');
  const [showNote, setShowNote] = useState<boolean>(false);

  // Опції для селектів
  const categoryOptions = dictionaries?.ticketCategories?.map(c => ({
    value: c,
    label: c.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
  })) || [];

  const statusOptions = dictionaries?.ticketStatuses?.map(s => ({
    value: s.id,
    label: s.name
  })) || [];

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
  ];

  // Фіктивні опції для Subject (можна пізніше замінити на реальні)
  const subjectOptions = [
    { value: 'plumbing_issue', label: 'Plumbing issue' },
    { value: 'electricity_issue', label: 'Electricity issue' },
    { value: 'ventilation_issue', label: 'Ventilation issue' },
    { value: 'other', label: 'Other' },
  ];

  /**
   * Обробляє зміни в полях форми
   */
  const handleFieldChange = (field: keyof UpdateTicketDto, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Обробляє збереження форми
   */
  const handleSave = () => {
    if (onSave) {
      const dataToSave = { ...formData };
      if (showNote && note.trim()) {
        // Додати логіку для нотатки якщо потрібно
      }
      onSave(dataToSave);
    }
  };

  /**
   * Обробляє очищення форми
   */
  const handleClear = () => {
    setFormData({
      category: ticket?.category,
      status: ticket?.status,
      priority: ticket?.priority,
    });
    setDueDate(ticket?.updatedAt ? ticket.updatedAt.split('T')[0] : '');
    setNote('');
    setShowNote(false);
    if (onClear) {
      onClear();
    }
  };

  const renderEditForm = () => (
    <div className={`${baseClass}__form`}>
      {/* Форма з полями */}
      <div className={`${baseClass}__fields-section`}>
        <div className={`${baseClass}__fields-table`}>
          {/* Ліва колонка - лейбли */}
          <div className={`${baseClass}__field-labels`}>
            <div className={`${baseClass}__field-label`}>Category:</div>
            <div className={`${baseClass}__field-label`}>Subject:</div>
            <div className={`${baseClass}__field-label`}>Due date:</div>
            <div className={`${baseClass}__field-label`}>Priority:</div>
            <div className={`${baseClass}__field-label`}>Status:</div>
          </div>

          {/* Права колонка - інпути */}
          <div className={`${baseClass}__field-inputs`}>
            <UISelect
              value={formData.category || ''}
              onChange={(e) => handleFieldChange('category', e.target.value as TicketCategory)}
              options={categoryOptions}
              className={`${baseClass}__select`}
              placeholder="Select Category"
            />

            <UISelect
              value={ticket?.title || ''}
              onChange={() => {}} // Placeholder
              options={subjectOptions}
              className={`${baseClass}__select`}
              placeholder="Select Subject"
            />

            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className={`${baseClass}__date-input`}
            />

            <UISelect
              value={formData.priority || ''}
              onChange={(e) => handleFieldChange('priority', e.target.value as TicketPriority)}
              options={priorityOptions}
              className={`${baseClass}__select`}
              placeholder="Select Priority"
            />

            <UISelect
              value={formData.status || ''}
              onChange={(e) => handleFieldChange('status', e.target.value as TicketStatus)}
              options={statusOptions}
              className={`${baseClass}__select`}
              placeholder="Select Status"
            />
          </div>
        </div>
      </div>

      {/* Секція з нотаткою */}
      <div className={`${baseClass}__note-section`}>
        <div className={`${baseClass}__note-checkbox`}>
          <input
            type="checkbox"
            id="showNote"
            checked={showNote}
            onChange={(e) => setShowNote(e.target.checked)}
            className={`${baseClass}__checkbox`}
          />
          <label htmlFor="showNote" className={`${baseClass}__checkbox-label`}>
            Note
          </label>
        </div>

        {showNote && (
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Specify"
            className={`${baseClass}__note-textarea`}
            rows={3}
          />
        )}
      </div>

      {/* Кнопки дій */}
      <div className={`${baseClass}__actions`}>
        <UIButton
          variant="primary"
          onClick={handleSave}
          disabled={loadingStates.saving}
        >
          {loadingStates.saving ? 'Saving...' : 'Save'}
        </UIButton>

        <UIButton
          variant="secondary"
          outline={true}
          onClick={handleClear}
        >
          Clear
        </UIButton>

        <UIButton
          variant="success"
          onClick={onCloseTicket}
          disabled={loadingStates.closing}
        >
          {loadingStates.closing ? 'Closing...' : 'Close ticket'}
        </UIButton>

        <UIButton
          variant="warning"
          onClick={onReopenTicket}
          disabled={loadingStates.reopening}
        >
          {loadingStates.reopening ? 'Reopening...' : 'Reopen ticket'}
        </UIButton>

        <UIButton
          variant="danger"
          onClick={onDeleteTicket}
          disabled={loadingStates.deleting}
        >
          {loadingStates.deleting ? 'Deleting...' : 'Delete ticket'}
        </UIButton>
      </div>
    </div>
  );

  return (
    <UICollapsibleSection
      title="Edit ticket"
      description=""
      collapsible={true}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      loading={loading}
      loadingText="Loading ticket form..."
      className={`${baseClass} ${className}`.trim()}
    >
      {renderEditForm()}
    </UICollapsibleSection>
  );
};

export default TicketEditSection;